import React, { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle2, AlertCircle, AlertTriangle, Info, Loader2 } from 'lucide-react';

type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  dismissible?: boolean;
}

interface ToastNotificationProps {
  toast: Toast;
  onDismiss: (id: string) => void;
  index: number;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({ toast, onDismiss, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Trigger fade-in animation
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(toast.id);
    }, 300); // Match animation duration
  }, [toast.id, onDismiss]);

  const getToastStyles = () => {
    const baseStyles = "flex items-start p-4 rounded-xl shadow-2xl border backdrop-blur-sm max-w-sm w-full transition-all duration-300 ease-out relative";
    
    switch (toast.type) {
      case 'success':
        return `${baseStyles} bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-900/40 dark:to-emerald-800/40 border-emerald-300 dark:border-emerald-700 text-emerald-900 dark:text-emerald-100 shadow-emerald-200/50`;
      case 'error':
        return `${baseStyles} bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/40 dark:to-red-800/40 border-red-300 dark:border-red-700 text-red-900 dark:text-red-100 shadow-red-200/50`;
      case 'warning':
        return `${baseStyles} bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/40 dark:to-orange-800/40 border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-100`;
      case 'info':
        return `${baseStyles} bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/40 dark:to-indigo-800/40 border-blue-200 dark:border-blue-700 text-blue-800 dark:text-blue-100`;
      case 'loading':
        return `${baseStyles} bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/40 dark:to-indigo-800/40 border-blue-200 dark:border-blue-700 text-blue-800 dark:text-blue-100`;
      default:
        return `${baseStyles} bg-[rgb(var(--surface))] border-[rgb(var(--border))] text-[rgb(var(--text-primary))]`;
    }
  };

  const getIcon = () => {
    const iconClass = "w-5 h-5 flex-shrink-0 mt-0.5";
    
    switch (toast.type) {
      case 'success':
        return <CheckCircle2 className={`${iconClass} text-emerald-600`} />;
      case 'error':
        return <AlertCircle className={`${iconClass} text-red-600`} />;
      case 'warning':
        return <AlertTriangle className={`${iconClass} text-amber-600`} />;
      case 'info':
        return <Info className={`${iconClass} text-blue-600`} />;
      case 'loading':
        return <Loader2 className={`${iconClass} text-blue-600 animate-spin`} />;
      default:
        return <Info className={`${iconClass} text-gray-600`} />;
    }
  };

  const transformStyle = isExiting 
    ? 'translate-x-full opacity-0 scale-95' 
    : isVisible 
      ? 'translate-x-0 opacity-100 scale-100' 
      : 'translate-x-full opacity-0 scale-95';

  return (
    <div
      className={`${getToastStyles()} transform ${transformStyle}`}
      style={{
        marginBottom: index > 0 ? '12px' : '0',
        zIndex: 9999 - index
      }}
    >
      <div className="flex items-start space-x-3 flex-1">
        {getIcon()}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-relaxed break-words">
            {toast.message}
          </p>
        </div>
      </div>
      
      {(toast.dismissible !== false) && (
        <button
          onClick={handleDismiss}
          className="ml-3 flex-shrink-0 p-1 rounded-lg hover:bg-black/10 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current"
          aria-label="Dismiss notification"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-6 right-6 z-[9999] space-y-0 pointer-events-none">
      <div className="flex flex-col-reverse space-y-reverse space-y-3 pointer-events-auto">
        {toasts.map((toast, index) => (
          <ToastNotification
            key={toast.id}
            toast={toast}
            onDismiss={onDismiss}
            index={index}
          />
        ))}
      </div>
    </div>
  );
};


